import { useCompanyStore } from "@/stores/company";
import { useEmployeeStore } from "@/stores/employees";
import { useAuthStore } from "@/stores/auth";

/**
 * Align Pinia company context with URL `companySlug` for slug-scoped routes
 * (dashboard, accounting, taxation, hr).
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {Promise<true | import('vue-router').RouteLocationRaw>}
 */
export async function syncRouteContext(to) {
  const companySlug = to.params.companySlug;

  if (!companySlug) {
    return true;
  }

  const companyStore = useCompanyStore();

  // Only fetch if companies aren't loaded yet. This avoids re-fetching (and
  // re-running setCompany -> autoSelectFirstEmployeeUsingLetters) on every
  // slug-scoped navigation, which races with URL-driven employee selection.
  if (companyStore.allCompanies.length === 0 && !companyStore.loading) {
    const result = await companyStore.getData(true, {
      preferredSlug: companySlug,
      skipEmployeeAutoSelect: true,
    });

    if (result?.code === "ERROR") {
      return { name: "NotFound" };
    }
  }

  const resolved = companyStore.companyUsingSlug(companySlug);
  if (!resolved) {
    return { name: "NotFound" };
  }

  // Align selected company with the URL slug without touching the employee store.
  if (resolved.id !== companyStore.selectedCompanyId) {
    companyStore.setCompany(resolved, { skipEmployeeAutoSelect: true });
  }

  // For client logins with multiple employee profiles, keep
  // `authStore.selectedEmployee` aligned with the URL's company so the
  // EmployeeComboBox display and role-based permission getters stay
  // coherent. Admins and single-profile clients are unaffected.
  const authStore = useAuthStore();
  if (authStore.employees.length > 1) {
    const currentEmpCompanyId = authStore.selectedEmployee?.company?.id;
    if (currentEmpCompanyId !== resolved.id) {
      const matchingProfile = authStore.employees.find(
        (e) => e.company?.id === resolved.id
      );
      if (matchingProfile) {
        authStore.setNewSelectedEmployee(matchingProfile);
      }
    }
  }

  return true;
}

/**
 * Ensure employees are loaded for the current company and resolve an
 * `:employeeId` route param to an employee in that company.
 *
 * Assumes `syncRouteContext` has already run (via the global `beforeEach`)
 * so `companyStore.selected` matches `to.params.companySlug`.
 *
 * - Returns the resolved employee (also sets it on the employee store)
 *   when the employee belongs to the current company.
 * - Returns `{ redirect: 'notFound' }` when the id does not match.
 * - When no `:employeeId` is present, returns `{ employee: null }` without
 *   touching the store (callers decide what to do, e.g. the company index
 *   route picks a default employee).
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 */
export async function syncHrRouteContext(to) {
  const employeeStore = useEmployeeStore();

  if (employeeStore.allEmployees.length === 0 && !employeeStore.loading) {
    await employeeStore.getData();
  }

  const employeeId = to.params.employeeId;

  if (!employeeId) {
    return { employee: null };
  }

  const employee = employeeStore.companyEmployees.find((e) => e.id === employeeId);
  if (!employee) {
    return { redirect: "notFound" };
  }

  if (employeeStore.lastSelected?.id !== employee.id) {
    employeeStore.setEmployee(employee);
  }

  return { employee };
}
